'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { Building2, ChevronDown, AlertCircle, Copy, Check } from 'lucide-react';
import { institutionNeedsSetup, type InstitutionWorkspaceRole } from '@/lib/institution-workspace';
import type { InstitutionWorkspaceOption } from '@/hooks/useInstitutionWorkspace';
import { INSTITUTION_HERITAGE_LIGHT } from '@/lib/institution-heritage-palette';

type Tokens = {
  surface: string;
  surfaceBorder: string;
  textPrimary: string;
  textMuted: string;
  accent: string;
  accentSoft: string;
  prismEdge?: string;
  /** Faint champagne (light) / aurora (dark) glow along the bottom edge -- a subtle
   * second edge distinct from `prismEdge`'s top rule, so this identity card reads as
   * a deliberately elevated panel rather than another content card. */
  edgeGlow?: string;
  shadow?: string;
};

type Props = {
  expanded: boolean;
  isOpal: boolean;
  th: Tokens;
  name: string;
  institutionId: string;
  publicHandle?: string | null;
  logoUrl: string | null;
  role: InstitutionWorkspaceRole;
  isDemoWorkspace?: boolean;
  workspaces: InstitutionWorkspaceOption[];
  profile: {
    institution_name?: string | null;
    full_name?: string | null;
    institution_id?: string | null;
  } | null;
  onSelectWorkspace?: (id: string) => void;
};

export default function InstitutionWorkspaceCard({
  expanded,
  isOpal,
  th,
  name,
  institutionId,
  publicHandle,
  logoUrl,
  role,
  isDemoWorkspace = false,
  workspaces,
  profile,
  onSelectWorkspace,
}: Props) {
  const [copied, setCopied] = useState(false);
  const setup = institutionNeedsSetup(profile);
  const displayName = name || (setup ? 'Institution setup' : 'Workspace');
  // Never show the raw workspace UUID in normal navigation — the public handle is the only
  // secondary identity line. Billing tier intentionally never appears on this card.
  const identityLine = publicHandle ? `@${publicHandle}` : '';
  const statusLabel = isDemoWorkspace ? 'Demo workspace' : 'Workspace active';
  const roleLabel = role || 'Member';
  const statusLine = `${statusLabel} · ${roleLabel}`;
  const copyWorkspaceId = async () => {
    if (!institutionId) return;
    try {
      await navigator.clipboard.writeText(institutionId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      // clipboard unavailable — no-op, nothing else to show for a copy action
    }
  };
  const initials = displayName
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  if (!expanded) {
    return (
      <div
        style={{
          margin: '8px 10px 4px',
          display: 'flex',
          justifyContent: 'center',
          position: 'relative',
        }}
        title={`${displayName} · ${statusLine}`}
      >
        {logoUrl ? (
          <img
            src={logoUrl}
            alt=""
            style={{
              width: 36,
              height: 36,
              borderRadius: 9,
              objectFit: 'cover',
              border: `1px solid ${th.surfaceBorder}`,
            }}
          />
        ) : (
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 9,
              background: th.accentSoft,
              border: `1px solid ${th.surfaceBorder}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: th.accent,
              fontSize: 11,
              fontWeight: 800,
            }}
          >
            {initials || <Building2 size={16} />}
          </div>
        )}
        {isDemoWorkspace && (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: -2,
              right: 4,
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: isOpal ? INSTITUTION_HERITAGE_LIGHT.goldDeep : '#D4AF5C',
              border: `1.5px solid ${th.surface}`,
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'relative',
        margin: '8px 10px 8px',
        padding: '10px 10px 9px',
        borderRadius: 9,
        background: th.surface,
        border: `1px solid ${th.surfaceBorder}`,
        boxShadow: th.shadow,
        overflow: 'hidden',
      }}
    >
      {th.prismEdge && th.prismEdge !== 'none' && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 2,
            background: th.prismEdge,
            opacity: 0.8,
          }}
        />
      )}
      {th.edgeGlow && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 1,
            background: th.edgeGlow,
          }}
        />
      )}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9 }}>
        {logoUrl ? (
          <img
            src={logoUrl}
            alt=""
            style={{
              width: 36,
              height: 36,
              borderRadius: 9,
              objectFit: 'cover',
              border: `1px solid ${th.surfaceBorder}`,
              flexShrink: 0,
            }}
          />
        ) : (
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 9,
              background: th.accentSoft,
              border: `1px solid ${th.surfaceBorder}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: th.accent,
              fontSize: 12,
              fontWeight: 800,
              flexShrink: 0,
            }}
          >
            {initials || <Building2 size={18} />}
          </div>
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <p
              style={{
                margin: 0,
                fontSize: 12,
                fontWeight: 700,
                color: th.textPrimary,
                lineHeight: 1.3,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={displayName}
            >
              {displayName}
            </p>
            {isDemoWorkspace && (
              <span
                style={{
                  flexShrink: 0,
                  fontSize: 8.5,
                  fontWeight: 800,
                  letterSpacing: '0.04em',
                  padding: '1px 5px',
                  borderRadius: 4,
                  background: isOpal ? 'rgba(226,196,119,0.2)' : 'rgba(212,175,92,0.2)',
                  color: isOpal ? INSTITUTION_HERITAGE_LIGHT.goldDeep : '#D4AF5C',
                }}
              >
                DEMO
              </span>
            )}
            {institutionId && (
              <button
                type="button"
                onClick={copyWorkspaceId}
                title="Copy workspace ID"
                aria-label="Copy workspace ID"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  width: 28,
                  height: 28,
                  padding: 0,
                  border: 'none',
                  background: 'transparent',
                  color: th.textMuted,
                  cursor: 'pointer',
                  marginLeft: 'auto',
                  borderRadius: 7,
                }}
              >
                {copied ? <Check size={11} /> : <Copy size={11} />}
              </button>
            )}
          </div>
          {identityLine && (
            <p
              style={{
                margin: '2px 0 0',
                fontSize: 10.5,
                color: th.textMuted,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {identityLine}
            </p>
          )}
          <p style={{ margin: '4px 0 0', fontSize: 10, color: th.textMuted, fontWeight: 600 }}>
            {statusLine}
          </p>
        </div>
      </div>

      {setup && (
        <Link
          href="/institution-dashboard/settings"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginTop: 10,
            padding: '6px 8px',
            borderRadius: 8,
            background: isOpal ? INSTITUTION_HERITAGE_LIGHT.goldTint : 'rgba(212,175,92,0.16)',
            border: isOpal ? '1px solid rgba(168,129,53,0.26)' : '1px solid rgba(212,175,92,0.28)',
            textDecoration: 'none',
            fontSize: 10,
            fontWeight: 600,
            color: isOpal ? INSTITUTION_HERITAGE_LIGHT.goldDeep : '#D4AF5C',
          }}
        >
          <AlertCircle size={12} />
          Complete workspace setup
        </Link>
      )}

      {workspaces.length > 1 && onSelectWorkspace && (
        <div style={{ marginTop: 10, position: 'relative' }}>
          <label
            style={{
              display: 'block',
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: th.textMuted,
              marginBottom: 4,
            }}
          >
            Workspace
          </label>
          <div style={{ position: 'relative' }}>
            <select
              value={
                workspaces.find((w) => w.institutionId === institutionId)?.id ?? workspaces[0]?.id
              }
              onChange={(e) => onSelectWorkspace(e.target.value)}
              aria-label="Switch institution workspace"
              style={{
                width: '100%',
                appearance: 'none',
                padding: '7px 28px 7px 10px',
                borderRadius: 8,
                border: `1px solid ${th.surfaceBorder}`,
                background: th.surface,
                color: th.textPrimary,
                fontSize: 11,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {workspaces.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
            <ChevronDown
              size={14}
              style={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
                color: th.textMuted,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
